import { Dialog, Transition, Menu } from '@headlessui/react';
import { Fragment, useState, useCallback } from 'react';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import ErrorNotification from '../common/ErrorNotification';
import AudioRecorder from '../common/AudioRecorder';
import { apiClient } from '../../services/apiClient';

const feedbackTypes = [
  { id: 'improvement', icon: '💡' },
  { id: 'bug', icon: '🐛' },
  { id: 'feature', icon: '✨' },
  { id: 'other', icon: '📝' }
];

export default function FeedbackModal({ isOpen, onClose }) {
  const [message, setMessage] = useState('');
  const [selectedType, setSelectedType] = useState('improvement');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const { t } = useTranslation('common');

  const handleTranscriptionUpdate = useCallback((textArray) => {
    const fullText = textArray.join(' ');
    setTranscriptSegments(textArray);
    setMessage(fullText);
  }, []);

  const handleRecordingError = useCallback((errorMessage) => {
    setError(errorMessage);
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError(t('feedback.error.messageRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      await apiClient.post('/api/feedback', {
        type: selectedType,
        message: message.trim()
      });

      setSuccess(true);
      setMessage('');
      setSelectedType('improvement');
      setTranscriptSegments([]);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(t('feedback.error.submit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ErrorNotification 
        show={!!error}
        message={error}
        onClose={() => setError(null)}
      />
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel 
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                  data-feedback-modal="true"
                >
                  <Dialog.Title
                    as="div"
                    className="flex justify-between items-center mb-4"
                  >
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('feedback.title')}
                    </h3>
                    {!success && (
                      <button
                        type="button"
                        className="rounded-md text-gray-400 hover:text-gray-500"
                        onClick={onClose}
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    )}
                  </Dialog.Title>

                  <form onSubmit={handleSubmit}>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('feedback.type')}
                      </label>
                      <Menu as="div" className="relative inline-block text-left w-full">
                        {({ open }) => (
                          <>
                            <Menu.Button 
                              className="inline-flex w-full justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100 disabled:opacity-50"
                              disabled={success || isSubmitting}
                            >
                              {t(`feedback.types.${selectedType}`)}
                              <ChevronDownIcon 
                                className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                                  open ? 'transform rotate-180' : ''
                                }`} 
                                aria-hidden="true" 
                              />
                            </Menu.Button>

                            <Menu.Items className="absolute left-0 z-10 mt-2 w-full origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              <div className="py-1">
                                {feedbackTypes.map((feedbackType) => (
                                  <Menu.Item key={feedbackType.id}>
                                    {({ active }) => (
                                      <button
                                        type="button"
                                        onClick={() => setSelectedType(feedbackType.id)}
                                        className={`${
                                          active ? 'bg-[rgb(197,214,248)]' : ''
                                        } ${
                                          selectedType === feedbackType.id ? 'bg-[rgb(197,214,248)]' : ''
                                        } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                      >
                                        {t(`feedback.types.${feedbackType.id}`)}
                                      </button>
                                    )}
                                  </Menu.Item>
                                ))}
                              </div>
                            </Menu.Items>
                          </>
                        )}
                      </Menu>
                    </div>

                    <div className="mt-4">
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {t('feedback.message')}
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:bg-gray-50"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t('feedback.placeholder')}
                        disabled={success || isSubmitting}
                      />
                    </div>

                    <div className="mt-4">
                      <AudioRecorder
                        onTranscriptionUpdate={handleTranscriptionUpdate}
                        onError={handleRecordingError}
                        priority="high"
                        transcriptSegments={transcriptSegments}
                      />
                    </div>

                    {error && (
                      <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
                        {error}
                      </div>
                    )}

                    {success && (
                      <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
                        {t('feedback.success')}
                      </div>
                    )}
                    
                    {!success && (
                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={onClose}
                          disabled={isSubmitting}
                          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                          {t('feedback.cancel')}
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                            ${!isSubmitting ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                            focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                        >
                          {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
                        </button>
                      </div>
                    )}
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
} 